
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import type { Application } from '@/types/database.types';
import { useEffect } from 'react';

interface MedicalCarrierInformationProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  data: Partial<Application>;
}

export const MedicalCarrierInformation = ({
  register,
  watch,
  setValue,
  data
}: MedicalCarrierInformationProps) => {
  useEffect(() => {
    // Initialize values on component mount only
    if (data) {
      // Handle current_medical_carrier initialization
      setValue('current_medical_carrier', data.current_medical_carrier === null ? '' : data.current_medical_carrier || '');
      
      if (data.next_medical_renewal) {
        setValue('next_medical_renewal', data.next_medical_renewal);
      }
      
      // For arrays like desired carriers - ensure it's initialized as empty array not null
      if (!data.desired_medical_carriers) {
        setValue('desired_medical_carriers', []);
      } else if (typeof data.desired_medical_carriers === 'string') {
        try {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(data.desired_medical_carriers as string);
          setValue('desired_medical_carriers', Array.isArray(parsed) ? parsed : [data.desired_medical_carriers]);
        } catch (e) {
          // If parsing fails, treat it as a single value
          setValue('desired_medical_carriers', [data.desired_medical_carriers]);
        }
      }
      
      // Ensure boolean fields have proper boolean values
      setValue('is_self_funded', Boolean(data.is_self_funded));
    }
  }, [data, setValue]);

  const handleDesiredCarrierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Convert comma-separated string to array when needed
    const carrierString = e.target.value || '';
    const carriers = carrierString.split(',').map(c => c.trim()).filter(Boolean);
    setValue('desired_medical_carriers', carriers);
  };

  const formatCarriersForDisplay = (carriers: string[] | string | null | undefined): string => {
    if (!carriers) return '';
    if (typeof carriers === 'string') {
      if (carriers.startsWith('[')) {
        try {
          const parsedCarriers = JSON.parse(carriers);
          return Array.isArray(parsedCarriers) ? parsedCarriers.join(', ') : '';
        } catch (e) {
          return carriers;
        }
      }
      return carriers;
    }
    return Array.isArray(carriers) ? carriers.join(', ') : '';
  };

  // Helper function to handle date conversion
  const handleDateSelect = (date: Date | undefined, fieldName: string) => {
    if (date) {
      // Format as ISO string for database storage
      setValue(fieldName as any, date.toISOString());
    } else {
      setValue(fieldName as any, undefined);
    }
  };

  // Parse the date from string to Date object for the DatePicker
  const parseDate = (dateString: string | null | undefined): Date | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
  };

  // Get the current value from the form
  const nextMedicalRenewal = watch('next_medical_renewal');
  const parsedDate = parseDate(nextMedicalRenewal);
  
  // Get current values for display
  const desiredMedicalCarriers = watch('desired_medical_carriers');
  const isSelfFunded = watch('is_self_funded');

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="current_medical_carrier">Current Medical Carrier</Label>
        <Input
          id="current_medical_carrier"
          placeholder="Enter current medical insurance carrier"
          {...register('current_medical_carrier')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="desired_medical_carriers">Desired Medical Carriers (comma separated)</Label>
        <Input
          id="desired_medical_carriers"
          value={formatCarriersForDisplay(desiredMedicalCarriers)}
          onChange={handleDesiredCarrierChange}
          placeholder="Blue Cross, UnitedHealthcare, etc."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="next_medical_renewal">Next Medical Renewal Date</Label>
        <DatePicker
          id="next_medical_renewal"
          date={parsedDate}
          onSelect={(date) => handleDateSelect(date, 'next_medical_renewal')}
          placeholder="Select renewal date"
        />
      </div>

      <div className="flex items-center space-x-2 mt-4">
        <Checkbox
          id="is_self_funded"
          checked={Boolean(isSelfFunded)}
          onCheckedChange={(checked) => setValue('is_self_funded', Boolean(checked))}
        />
        <Label htmlFor="is_self_funded" className="cursor-pointer">
          Self-funded medical plan
        </Label>
      </div>
    </div>
  );
};
